import { TYPES } from '@Types';
import {
  ArsenalUnitRepository,
  RequestArsenalUnitSuccess,
} from '@Repository/arsenal';
import { inject, injectable } from 'inversify';
import { Result } from '@Redux/type';
import {
  ArsenalArmy,
  ArsenalUnit,
  ArsenalUnitStep,
  PaintStep,
  SyncEntityState,
} from '@Models';
import { ILoadingEntityAction } from '@Redux/ILoadingEntityAction';
import arsenalArmySlice from '@Redux/reducers/arsenal/arsenal-army.slice';
import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { AvailableUnitView } from '@Selectors/builder/myArmyList/make/unit/new/view/AvailableUnitCategoryView';
import { Action, AppState } from '@Redux/store';
import { ThunkDispatch } from 'redux-thunk';
import { ArsenalUnitStepAction } from '@Redux/actions/arsenal/arsenal-unit-step.action';

const POOL_TIME = 2500;

@injectable()
export class ArsenalUnitAction extends ILoadingEntityAction {
  @inject(TYPES.Repository.ArsenalUnitRepository)
  private _repository!: ArsenalUnitRepository;

  public updateArmyStep = createAsyncThunk(
    `${this.constructor.name}/updateArmyStep`,
    async (_, thunkAPI) => {
      thunkAPI.dispatch(this.areLoading(true));

      const army = (thunkAPI.getState() as AppState).arsenalArmy.entity;

      if (!army) {
        return;
      }

      const steps = army.steps.map(
        (s) => ({ ...s, size: 0 }) satisfies ArsenalUnitStep as ArsenalUnitStep,
      );

      army.units
        .flatMap((unit) => unit.steps)
        .forEach((step) => {
          let s = steps.find((s) => s.step === step.step);

          if (!s) {
            s = {
              id: uuidv4(),
              step: step.step,
              size: 0,
              state: SyncEntityState.ToCreate,
            } satisfies ArsenalUnitStep as ArsenalUnitStep;
            steps.push(s);
          }

          s.size += step.size;
        });

      thunkAPI.dispatch(
        this.fetchDataSuccess({
          ...army,
          steps: steps,
        }),
      );

      thunkAPI.dispatch(this.areLoading(false));
    },
  );
  private stepPool = new Map<
    string,
    { timer: any; unitId: string; step: ArsenalUnitStep }
  >();

  public update = createAsyncThunk(
    `${this.constructor.name}/update`,
    async ({ unit }: { unit: ArsenalUnit }, thunkAPI) => {
      thunkAPI.dispatch(this.areLoading(true));

      const response = await this._repository.update(unit, { locale: null });

      if (
        this.handleFailure(
          response,
          thunkAPI.dispatch as ThunkDispatch<AppState, undefined, Action>,
        )
      )
        return;

      const success = response as RequestArsenalUnitSuccess;

      const arsenal = (thunkAPI.getState() as AppState).arsenalArmy.entity!!;
      const units = arsenal.units.map((item: ArsenalUnit) => {
        if (item.id === unit.id) {
          return success.payload;
        }
        return item;
      });

      thunkAPI.dispatch(
        this.fetchDataSuccess({
          ...arsenal,
          units: units,
        }),
      );
      thunkAPI.dispatch(this.areLoading(false));
    },
  );

  public delete = createAsyncThunk(
    `${this.constructor.name}/delete`,
    async ({ id }: { id: string }, thunkAPI) => {
      thunkAPI.dispatch(this.areLoading(true));

      const response = await this._repository.delete(id);

      if (
        this.handleFailure(
          response,
          thunkAPI.dispatch as ThunkDispatch<AppState, undefined, Action>,
        )
      )
        return;

      const arsenal = (thunkAPI.getState() as AppState).arsenalArmy.entity!!;
      thunkAPI.dispatch(
        this.fetchDataSuccess({
          ...arsenal,
          units: arsenal.units.filter((item) => item.id != id),
        }),
      );
      thunkAPI.dispatch(this.areLoading(false));
    },
  );

  @inject(TYPES.Actions.ArsenalUnitStepAction)
  private _stepAction!: ArsenalUnitStepAction;

  public changeStep = createAsyncThunk(
    `${this.constructor.name}/changeStep`,
    async (
      {
        unitId,
        step,
        change,
      }: { unitId: string; step: PaintStep; change: number },
      thunkAPI,
    ) => {
      thunkAPI.dispatch(this.areLoading(true));

      const army = (thunkAPI.getState() as AppState).arsenalArmy.entity;

      if (!army) {
        return;
      }

      const units = army.units.map((unit) => {
        if (unit.id !== unitId) {
          return unit;
        }

        const steps = unit.steps.map((s) => {
          if (s.step !== step) {
            return s;
          }

          return {
            ...s,
            size: change,
            state:
              s.state === SyncEntityState.Synced
                ? SyncEntityState.ToUpdate
                : s.state,
          } satisfies ArsenalUnitStep as ArsenalUnitStep;
        });
        const s: ArsenalUnitStep | undefined = steps.find(
          (s) => s.step === step,
        );
        if (!s) {
          steps.push({
            id: uuidv4(),
            step: step,
            size: change,
            state: SyncEntityState.ToCreate,
          } satisfies ArsenalUnitStep as ArsenalUnitStep);
        }

        steps.forEach((s) => {
          if (s.size < 0) {
            s.size = 0;
          }
        });

        return {
          ...unit,
          steps: steps,
        } satisfies ArsenalUnit as ArsenalUnit;
      });

      thunkAPI.dispatch(
        this.fetchDataSuccess({
          ...army,
          units: units,
        }),
      );

      thunkAPI.dispatch(this.updateArmyStep());

      units.forEach((unit) => {
        unit.steps.forEach((step) => {
          switch (step.state) {
            case SyncEntityState.ToCreate:
              this.registerPoolAction(unit, step, () => {
                thunkAPI.dispatch(
                  this._stepAction.create({ unitId: unit.id, step: step }),
                );
              });
              break;
            case SyncEntityState.ToUpdate:
              this.registerPoolAction(unit, step, () => {
                thunkAPI.dispatch(
                  this._stepAction.update({ unitId: unit.id, step: step }),
                );
              });
              break;
          }
        });
      });

      thunkAPI.dispatch(this.areLoading(false));
    },
  );

  public create =
    (unit: AvailableUnitView): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const arsenal = getState().arsenalArmy.entity!!;

      const arsenalUnit = {
        id: uuidv4(),
        name: unit.name,
        comment: null,
        unitCategoryId: unit.id,
        position: arsenal.units.length + 1,
        mountCategoryId: null,
        unitIds: [],
        steps: [],
      } satisfies ArsenalUnit as ArsenalUnit;

      dispatch(
        this.fetchDataSuccess({
          ...arsenal,
          units: arsenal.units.concat([arsenalUnit]),
        }),
      );

      const response = await this._repository.create(arsenal.id, arsenalUnit, {
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      dispatch(this.areLoading(false));
    };

  protected fetchDataSuccess(item: ArsenalArmy): PayloadAction<ArsenalArmy> {
    return arsenalArmySlice.actions.setArsenalArmy(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return arsenalArmySlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return arsenalArmySlice.actions.setLoading(bool);
  }

  private registerPoolAction(
    unit: ArsenalUnit,
    step: ArsenalUnitStep,
    call: () => void,
  ): void {
    this.stepPool.forEach((value, key) => {
      if (key === step.id) {
        clearTimeout(value.timer);
        this.stepPool.delete(key);
      }
    });
    this.stepPool.set(step.id, {
      timer: setTimeout(() => {
        call();
        this.stepPool.delete(step.id);
      }, POOL_TIME),
      unitId: unit.id,
      step: step,
    });
  }
}
